import React, { useEffect, useRef, useState } from 'react';
import { Check, Clock, Coins, Film, Layers } from 'lucide-react';
import { TaskyButton, TaskyDialog } from '../../components/tasky';
import type { RecipeVideoStyle } from '../../services/api/recipeVideoService';
import { useRecipeVideoStrings } from './strings';

/**
 * 動画スタイルを選ぶモーダル。バナーの「スタイルを選ぶ」（components/image-generation/StyleSelectModal）と
 * 同じ並び（タイトル＋説明・カードのグリッド・選択中＋キャンセル／適用）とカードの見た目で作る。
 *
 * StyleSelectModal は画像スタイル専用（スタイル一覧 API・タグ・おすすめ・評価ボタン）で中身を流用できないため、
 * 枠は Tasky の TaskyDialog（Esc・背景クリックで閉じる・スクロール固定）を使い、カードのクラス構成を揃えた別部品にしている
 * （バナー側の挙動は変えない。手書きモーダルを増やさない）。
 * 動画は必ずスタイルが要るので「スタイルなしで続行」は無く、1つだけ選ぶ。
 * 管理画面で見本を作ったスタイルは、カードに静止画を出し、ホバー中・選択中だけ音なしのダイジェストを流す
 * （preload="none" で一覧を開いただけでは動画を読まない。動きを減らす設定では流さない）。
 */
interface RecipeVideoStyleSelectModalProps {
  isOpen: boolean;
  styles: RecipeVideoStyle[];
  selectedId: number | null;
  onClose: () => void;
  onSelect: (style: RecipeVideoStyle) => void;
}

const CARD_BASE = 'h-auto w-full flex-col items-stretch justify-start gap-0 overflow-hidden whitespace-normal rounded-xl border-2 bg-gray-50 p-0 text-left font-normal transition-all hover:-translate-y-0.5';
const CARD_SELECTED = `${CARD_BASE} border-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.2)] hover:bg-gray-50`;
const CARD_IDLE = `${CARD_BASE} border-gray-200 hover:border-blue-500 hover:bg-gray-50 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]`;

const prefersReducedMotion = () => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** カードの絵: 見本のダイジェスト（静止画をポスターに）→ 静止画だけ → アイコン */
const StyleCardMedia: React.FC<{ style: RecipeVideoStyle; playing: boolean }> = ({ style, playing }) => {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    // 自動再生が拒否されても（省電力モード等）静止画が出ているので握りつぶしてよい
    if (playing) video.play()?.catch(() => undefined);
    else video.pause();
  }, [playing]);

  if (style.sample_video_url) {
    return (
      <video
        ref={ref}
        src={style.sample_video_url}
        poster={style.thumbnail_url ?? undefined}
        preload="none"
        muted
        loop
        playsInline
        aria-hidden
        data-testid="recipe-video-style-sample"
        className="block aspect-[9/16] w-full object-cover"
      />
    );
  }
  if (style.thumbnail_url) {
    return <img src={style.thumbnail_url} alt="" data-testid="recipe-video-style-thumbnail" className="block aspect-[9/16] w-full object-cover" />;
  }
  return (
    <div data-testid="recipe-video-style-icon" className="flex aspect-[9/16] w-full items-center justify-center text-gray-400">
      <Film size={32} />
    </div>
  );
};

const RecipeVideoStyleSelectModal: React.FC<RecipeVideoStyleSelectModalProps> = ({ isOpen, styles, selectedId, onClose, onSelect }) => {
  const t = useRecipeVideoStrings();
  const [picked, setPicked] = useState<number | null>(selectedId);
  const [hovered, setHovered] = useState<number | null>(null);
  const [reducedMotion] = useState(prefersReducedMotion);

  // 開くたびに今の選択から始める
  useEffect(() => {
    if (isOpen) setPicked(selectedId);
  }, [isOpen, selectedId]);

  const pickedStyle = styles.find((s) => s.id === picked) ?? null;

  return (
    <TaskyDialog
      open={isOpen}
      onClose={onClose}
      // バナーの StyleSelectModal と同じ大きさ・角丸
      className="max-h-[85vh] max-w-[1280px] rounded-2xl"
      title={
        <span className="flex flex-col items-start gap-1">
          <span className="text-[18px] font-semibold text-gray-800">{t.styleModalTitle}</span>
          <span className="text-[13px] font-normal text-gray-500">{t.styleModalLead}</span>
        </span>
      }
      footer={
        <div className="flex w-full items-center justify-between gap-4 max-[640px]:flex-col max-[640px]:items-stretch">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {pickedStyle && (
              <>
                <span className="shrink-0 text-[13px] text-gray-500">{t.selected}</span>
                <span className="text-sm font-medium text-gray-800">{pickedStyle.name}</span>
              </>
            )}
          </div>
          <div className="flex shrink-0 gap-3 max-[640px]:justify-end">
            <TaskyButton type="button" variant="outline" onClick={onClose} className="rounded-lg border-gray-300 bg-white px-5 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-800">
              {t.cancel}
            </TaskyButton>
            <TaskyButton
              type="button"
              disabled={pickedStyle === null}
              onClick={() => pickedStyle && onSelect(pickedStyle)}
              className="rounded-lg bg-blue-500 px-6 text-sm text-white hover:bg-blue-600"
            >
              {t.applyStyle}
            </TaskyButton>
          </div>
        </div>
      }
    >
      {styles.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-sm text-gray-400">
          <Film size={48} className="opacity-50" />
          <p>{t.noStyles}</p>
        </div>
      ) : (
        <div className="grid gap-3 py-2 grid-cols-[repeat(auto-fill,minmax(180px,1fr))] max-[640px]:grid-cols-1" role="radiogroup" aria-label={t.style}>
          {styles.map((style) => {
            const isSelected = style.id === picked;
            return (
              <TaskyButton
                key={style.id}
                type="button"
                variant="ghost"
                role="radio"
                aria-checked={isSelected}
                aria-label={style.name}
                onClick={() => setPicked(style.id)}
                onPointerEnter={() => setHovered(style.id)}
                onPointerLeave={() => setHovered((current) => (current === style.id ? null : current))}
                className={isSelected ? CARD_SELECTED : CARD_IDLE}
              >
                <div className="relative block w-full">
                  <StyleCardMedia style={style} playing={!reducedMotion && (isSelected || hovered === style.id)} />
                  {isSelected &&
                    (style.sample_video_url ? (
                      // 選択中は見本を流すので、全面を覆わず右上の印にする
                      <div className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-blue-500 text-white shadow">
                        <Check size={16} />
                      </div>
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-blue-500/60 text-white">
                        <Check size={24} />
                      </div>
                    ))}
                </div>
                <div className="flex flex-col p-3">
                  <h3 className="m-0 mb-2 block w-full text-[13px] font-semibold leading-[1.3] text-gray-800">{style.name}</h3>
                  <p className="mb-2 text-[11px] leading-[1.4] text-gray-500">{style.description}</p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded bg-gray-100 px-2 py-[3px] text-[11px] text-gray-500">
                      <Layers size={11} />
                      {t.scenes(style.scene_count)}
                    </span>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded bg-gray-100 px-2 py-[3px] text-[11px] text-gray-500">
                      <Clock size={11} />
                      {t.approxSeconds(style.approx_seconds)}
                    </span>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded bg-violet-50 px-2 py-[3px] text-[11px] text-violet-700">
                      <Coins size={11} />
                      {t.estimate(style.estimated_credits)}
                    </span>
                  </div>
                </div>
              </TaskyButton>
            );
          })}
        </div>
      )}
    </TaskyDialog>
  );
};

export default RecipeVideoStyleSelectModal;
